/**
 * <tyg-switcher>
 * Custom element som låter kunden byta mellan olika tyg-produkter
 * utan att hela sidan laddas om.
 *
 * Vid klick på en swatch:
 *   1. Hämta målproduktens main-product section via Section Rendering API
 *   2. Ersätt innehållet i nuvarande Shopify-section
 *   3. Uppdatera URL via history.pushState
 *   4. Uppdatera document.title
 *   5. Återinitiera dynamiska komponenter (Shopify Custom Elements gör detta automatiskt
 *      när noderna sätts in i DOM:en).
 *
 * Fallback: om något går fel följs länken på vanligt sätt (full page nav).
 *
 * SEO-not: <head>-taggar (canonical, og:*, JSON-LD product schema) uppdateras inte
 * vid AJAX-swap. Detta är medvetet — sökmotorer och social-share crawlers hämtar
 * varje produkt-URL färskt, så de får alltid korrekt metadata. Endast en användare
 * som "kopiera länk" mitt i en swap kan sprida en URL vars *nästa* besökare ändå
 * får färsk metadata. Inget att åtgärda.
 */
class TygSwitcher extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.abortController = null;
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.abortController) this.abortController.abort();
  }

  onClick(event) {
    const link = event.target.closest('.tyg-switcher__item');
    if (!link) return;
    if (link.classList.contains('tyg-switcher__item--active')) {
      event.preventDefault();
      return;
    }

    // Modifier-tangenter eller mittklick = låt browsern hantera (öppna i ny flik etc.)
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    const url = link.dataset.productUrl;
    if (!url) return;

    this.swap(url, link);
  }

  async swap(productUrl, clickedLink) {
    this.setLoading(true, clickedLink);

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const fetchUrl = `${productUrl}?section_id=${this.sectionId}`;
      const response = await fetch(fetchUrl, { signal: this.abortController.signal });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      const currentSectionEl = document.getElementById(`shopify-section-${this.sectionId}`);
      if (!currentSectionEl) throw new Error('Current section not found');

      const preservedState = this.captureFormState(currentSectionEl);

      currentSectionEl.innerHTML = wrapper.innerHTML;

      // Skippa scroll-trigger-animationer (slide-in/fade-in) på det nyligen
      // infogade innehållet — det är inte en första-rendering, det är en swap.
      currentSectionEl
        .querySelectorAll('.scroll-trigger')
        .forEach((el) => el.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in'));

      this.restoreFormState(currentSectionEl, preservedState);

      // Uppdatera "companion"-sektioner som visar produktspecifik data men
      // ligger utanför main-product (t.ex. main-product-content med
      // beskrivning + egenskaper). Markeras med data-tyg-companion-section.
      this.swapCompanionSections(productUrl);

      // Centrera aktiv swatch i listan horisontellt (utan att röra vertikal scroll).
      // Annars hamnar scrollLeft på 0 efter swap och användaren tappar bort sin
      // valda tygs position — extra störande på mobil med horisontell scroll.
      const newList = currentSectionEl.querySelector('.tyg-switcher__list');
      const newActive = currentSectionEl.querySelector('.tyg-switcher__item--active');
      if (newList && newActive) {
        const target = newActive.offsetLeft - (newList.clientWidth - newActive.offsetWidth) / 2;
        newList.scrollLeft = Math.max(0, target);
      }

      const newTitle = this.extractTitle(currentSectionEl) || document.title;

      const cleanUrl = new URL(productUrl, window.location.origin).pathname;
      window.history.pushState({ tygSwitch: true, url: cleanUrl }, '', cleanUrl);
      if (newTitle) document.title = newTitle;

      // Notifiera ev. lyssnare (Foxify, Judge.me, GA etc.)
      this.publishSwap(productUrl);

      // Scrolla inte; kunden ska känna att den är kvar på samma sida
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('[tyg-switcher] swap failed, falling back to navigation:', err);
      window.location.href = productUrl;
    } finally {
      this.setLoading(false, clickedLink);
    }
  }

  captureFormState(sectionEl) {
    // Vi bevarar bara quantity över byten. Variant-options (storlek, färg etc.)
    // är knutna till URL:en (?variant=id) som vi inte styr över här — om vi
    // ändrar option-värden post-swap riskerar de att hamna i konflikt med den
    // URL Shopify just bytte till. Kunden får alltid det nya tygets default.
    const qtyInput = sectionEl.querySelector('input[name="quantity"]');
    const quantity = qtyInput ? qtyInput.value : null;
    return { quantity };
  }

  restoreFormState(sectionEl, state) {
    if (!state || state.quantity == null) return;
    const qtyInput = sectionEl.querySelector('input[name="quantity"]');
    if (qtyInput && qtyInput.value !== state.quantity) {
      qtyInput.value = state.quantity;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  async swapCompanionSections(productUrl) {
    const companions = document.querySelectorAll('[data-tyg-companion-section]');
    if (!companions.length) return;

    const sectionIds = new Set();
    const wrappers = new Map();

    companions.forEach((inner) => {
      const sectionId = inner.dataset.sectionId;
      const wrapper = inner.closest('[id^="shopify-section-"]');
      if (!sectionId || !wrapper) return;
      sectionIds.add(sectionId);
      wrappers.set(sectionId, wrapper);
    });

    if (!sectionIds.size) return;

    await Promise.all(
      Array.from(sectionIds).map(async (sectionId) => {
        try {
          const response = await fetch(`${productUrl}?section_id=${sectionId}`);
          if (!response.ok) return;
          const html = await response.text();
          const wrapper = wrappers.get(sectionId);
          if (wrapper) wrapper.innerHTML = html;
        } catch (_) {
          /* tyst fallback — main-swap har redan lyckats */
        }
      })
    );
  }

  extractTitle(sectionEl) {
    const titleEl = sectionEl.querySelector('.product__title h1, .product__title, h1');
    if (!titleEl) return null;
    const productTitle = titleEl.textContent.trim();
    return productTitle ? `${productTitle} - Ooaki Living` : null;
  }

  setLoading(isLoading, clickedLink) {
    this.classList.toggle('tyg-switcher--loading', isLoading);
    if (clickedLink) {
      clickedLink.classList.toggle('tyg-switcher__item--pending', isLoading);
    }
    const liveRegion = this.querySelector('.tyg-switcher__loading');
    if (liveRegion) {
      liveRegion.textContent = isLoading ? 'Laddar tyg…' : '';
    }
  }

  publishSwap(url) {
    document.dispatchEvent(
      new CustomEvent('tyg-switcher:swap', {
        bubbles: true,
        detail: { url, sectionId: this.sectionId },
      })
    );
  }
}

if (!customElements.get('tyg-switcher')) {
  customElements.define('tyg-switcher', TygSwitcher);
}

// Hantera browser back/forward; ladda om motsvarande produkt via samma swap-flöde
window.addEventListener('popstate', (event) => {
  if (!event.state || !event.state.tygSwitch) return;
  const switcher = document.querySelector('tyg-switcher');
  if (!switcher) {
    window.location.reload();
    return;
  }
  switcher.swap(event.state.url, null);
});
